import {faSearch} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  Button,
  ContentLayout,
  Content,
  InputBox,
  ListTable,
  SearchFilter,
  SortableRow,
  Modal,
} from "@adminComponents";
import * as Datas from "@adminData";
import React, {useEffect, useMemo, useState} from "react";
import {useRecoilState, useSetRecoilState} from "recoil";
import {states} from "@recoils";
import {useAdminNav} from "@hook";
import {adminApi} from "@utils";
import styles from "./style.module.scss";
import {AdminScreenId, Svgs} from "@assets";
import _ from "lodash";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

export default function Page() {
  const setAlertModal = useSetRecoilState(states.alertState);
  const {getParams, navigate} = useAdminNav();
  const {searchTxt: paramsSearchTxt, sequnce: paramsSequnce} = getParams<{
    searchTxt?: string;
    sequnce?: number;
  }>();
  const [searchTxt, setSearchTxt] = useState(paramsSearchTxt);
  const [listData, setListData] =
    useState<Datas.ResultPagingResponse<Datas.IMemberResponse[]>>();
  const [maxSequnce, setMaxSequnce] = useState<number>();
  const [sequnceList, setSequnceList] = useState<number[]>([]);
  const [sequnce, setSequnce] = useRecoilState(states.memberSequnceState);
  const [isOpenSequnceModal, setIsOpenSequnceModal] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));

  const SequnceFilter: JSX.Element = useMemo(() => {
    return (
      <div className={styles.sequnceContainer}>
        {sequnceList.map(seq => (
          <div
            key={seq}
            onClick={() => setSequnce(seq)}
            className={[
              styles.sequnceItem,
              sequnce === seq ? styles.active : undefined,
            ].join(" ")}>
            {seq}기
          </div>
        ))}
      </div>
    );
  }, [sequnceList, sequnce]);

  async function getSequnce() {
    try {
      const {data, success, message} = await adminApi.info.getDetail({
        type: "sequnce",
      });
      if (success) {
        const maxSeqStr = (data.result || {}).content;
        const maxSeq = isNaN(Number(maxSeqStr)) ? 1 : Number(maxSeqStr);
        setMaxSequnce(maxSeq);
        setSequnceList(Array.from({length: maxSeq}, (_, index) => index + 1));
        setSequnce(prev => prev || maxSeq);
      } else {
        alert(message || "오류가 발생했습니다");
      }
    } catch (error) {
      console.error("getDetail error", error);
    }
  }

  async function getList({page = 1}: {page?: number} = {}) {
    try {
      if (!sequnce) {
        return;
      }
      const {data, success, message} = await adminApi.member.getList({
        sequnce,
        page,
        limit: 100000,
        searchTxt,
      });
      if (success) {
        setListData(data);
      } else {
        setAlertModal({
          isVisible: true,
          title: message || "오류가 발생했습니다",
        });
      }
    } catch (error) {
      console.error("getList error", error);
    }
  }

  function goToDetail({data}: {data?: Datas.IMember} = {}) {
    navigate({
      url: AdminScreenId.MEMBER_DETAIL,
      params: {no: data?.no, ...(!data ? {sequnce} : {})},
      state: {data},
    });
  }

  function goToListPage(params: {
    searchTxt?: string;
    sequnce?: number;
    page?: number;
  }) {
    navigate({
      url: AdminScreenId.MEMBER,
      params,
    });
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!sequnce) {
      return;
    }
    const {active, over} = event;
    if (!over) return;
    if (active.id === over.id) return;

    const prevList = listData?.result || [];
    const oldIndex = prevList.findIndex(item => item.no === active.id);
    const newIndex = prevList.findIndex(item => item.no === over.id);
    const nextList = arrayMove(prevList, oldIndex, newIndex);
    setListData(prev => ({...prev, result: nextList}));

    try {
      const newSort = nextList.map((item, index) => ({
        no: item.no!,
        ordering: index + 1,
      }));
      const {success, message} = await adminApi.member.sort({
        sequnce,
        data: newSort,
      });
      if (success) {
        //
      } else {
        alert(message || "오류가 발생했습니다");
      }
    } catch (error) {
      console.error("handleDragEnd error", error);
    }
  };

  async function sequnceSave(newValue: number) {
    try {
      if (!!maxSequnce && newValue <= maxSequnce) {
        alert("이미 등록된 기수입니다.");
        return;
      }

      const {success, message} = await adminApi.info.save([
        {type: "sequnce", content: String(newValue)},
      ]);
      if (success) {
        alert("등록완료");
        setIsOpenSequnceModal(false);
        getSequnce();
      } else {
        alert(message);
      }
    } catch (error) {
      console.error("sequnceSave error", error);
    }
  }

  useEffect(() => {
    getSequnce();
  }, []);

  useEffect(() => {
    getList();
  }, [sequnce, location.search]);

  useEffect(() => {
    if (!isNaN(Number(paramsSequnce))) {
      setSequnce(Number(paramsSequnce));
    }
  }, [location.search]);

  return (
    <>
      <div className={styles.container}>
        <ContentLayout title={"회원현황"}>
          <SearchFilter
            left={[
              <InputBox
                key={1}
                value={searchTxt}
                onValue={setSearchTxt}
                onEnter={() => {
                  goToListPage({page: 1, searchTxt, sequnce});
                }}
                rightContent={
                  <div
                    style={{display: "flex"}}
                    onClick={() => {
                      goToListPage({page: 1, searchTxt, sequnce});
                    }}>
                    <FontAwesomeIcon icon={faSearch} />
                  </div>
                }
              />,
            ]}
            right={[
              <Button
                key={0}
                text="기수등록"
                onClick={() => setIsOpenSequnceModal(true)}
              />,
              <Button key={1} text="신규등록" onClick={goToDetail} />,
            ]}
            bottom={SequnceFilter}
          />
          <Content>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}>
              <ListTable
                paging={listData?.page}
                getList={({page: newPage}) => {
                  goToListPage({
                    page: newPage,
                    searchTxt,
                  });
                }}>
                <thead>
                  <tr>
                    <th>순서</th>
                    <th>구분</th>
                    <th>이름</th>
                    <th>직책</th>
                    <th>연락처</th>
                    <th>코치</th>
                    <th>회사명</th>
                    <th>하는업무</th>
                    <th>회사주소</th>
                  </tr>
                </thead>
                <tbody>
                  <SortableContext
                    items={(listData?.result || []).map(item =>
                      String(item.no),
                    )}
                    strategy={verticalListSortingStrategy}>
                    {(listData?.result || []).length === 0 ? (
                      <tr>
                        <td colSpan={9}>조회된 내용이 없습니다.</td>
                      </tr>
                    ) : (
                      <>
                        {(listData?.result || []).map(item => {
                          return (
                            <SortableRow
                              key={item.no}
                              item={{...item, no: item.no!}}
                              format={item => (
                                <>
                                  <td>{item.no}</td>
                                  <td>{item.name}</td>
                                  <td>{item.position}</td>
                                  <td>{item.tel}</td>
                                  <td>{item.coach}</td>
                                  <td>{item.company}</td>
                                  <td>{item.company_work}</td>
                                  <td>{item.company_address}</td>
                                </>
                              )}
                              onClick={item => goToDetail({data: item})}
                            />
                          );
                        })}
                      </>
                    )}
                  </SortableContext>
                </tbody>
              </ListTable>
            </DndContext>
          </Content>
        </ContentLayout>
      </div>
      <SequnceModal
        visible={isOpenSequnceModal}
        close={() => setIsOpenSequnceModal(false)}
        sequnce={maxSequnce}
        save={sequnceSave}
      />
    </>
  );
}

function SequnceModal({
  visible,
  close,
  sequnce = 1,
  save,
}: {
  visible?: boolean;
  close?: () => void;
  sequnce?: number;
  save: (sequnce: number) => void;
}) {
  const [value, setValue] = useState(sequnce + 1);

  useEffect(() => {
    setValue(sequnce + 1);
  }, [sequnce, visible]);

  return (
    <Modal close={close} visible={visible} className={styles.modalContainer}>
      <div className={styles.modal}>
        <div className={styles.close} onClick={close}>
          <Svgs.Close width={15} />
        </div>
        <div className={styles.modalContainer}>
          <div className={styles.title}>기수 등록</div>
          <div className={styles.content}>
            <InputBox
              type="number"
              value={value}
              onValue={txt => setValue(Number(txt))}
            />
          </div>
          <div className={styles.buttonContainer}>
            <Button text="닫기" onClick={close} />
            <Button
              text="저장"
              backgroundColor="gray"
              onClick={() => save(value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
